/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactNode, type CSSProperties } from 'react';
import { Link } from '@/components/link';
import { Carousel, CarouselContent, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { cn, parseHexColor } from '@/lib/utils';
import { useScopedStyleOverride } from '@/hooks/use-scoped-style-override';
import {
    type Typography,
    FONT_FAMILY_CLASS,
    normalizeTypography,
    normalizeFontFamily,
} from '@/lib/page-designer-typography';

export interface CarouselSectionProps {
    /** CSS fragment scoped to this carousel instance (the `styleOverride` Page Designer attribute), forwarded by callers such as `ProductCarousel`/`PopularCategories`. */
    styleOverride?: string;
    /** Optional title displayed above the carousel */
    title?: string;
    /** Optional subtitle displayed below the title */
    subtitle?: string;
    /**
     * Optional "Shop all" link URL.
     * @remarks Ignored when `titleAlign` is `'center'`.
     */
    shopAllUrl?: string;
    /**
     * Optional label for the "Shop all" link.
     * @remarks Ignored when `titleAlign` is `'center'`.
     */
    shopAllText?: string;
    /** Optional className override for the title heading */
    titleClassName?: string;
    /** Hex color for the title (e.g. #FFFFFF or #fff) */
    titleColor?: string;
    /** Visual typography preset for the title; also see `titleClassName` for a full class override */
    titleTypography?: string;
    /** Visual font family for the title, independent of `titleTypography` */
    titleFontFamily?: string;
    /** Visual typography preset for the subtitle */
    subtitleTypography?: string;
    /** Hex color for the subtitle (e.g. #FFFFFF or #fff) */
    subtitleColor?: string;
    /** Visual font family for the subtitle, independent of `subtitleTypography` */
    subtitleFontFamily?: string;
    /**
     * Title alignment.
     * - 'left' (default): title left-aligned with optional shop-all link on the right
     * - 'center': title and subtitle centered, no shop-all link
     */
    titleAlign?: 'left' | 'center';
    /** Optional className for the outer wrapper div */
    className?: string;
    /** Accessible label for the carousel region */
    ariaLabel?: string;
    /** Carousel items — should be <CarouselItem> elements */
    children: ReactNode;
}

const defaultTitleBaseClassName = 'text-4xl font-medium leading-none tracking-[-0.9px]';

// Non-Default typography values override the size/weight/tracking utilities above via twMerge —
// left unset ('Default') so the existing look is preserved exactly when authors don't opt in.
const TITLE_TYPOGRAPHY_CLASS: Partial<Record<Typography, string>> = {
    Paragraph: 'text-base font-normal leading-7',
    'Heading 1': 'text-4xl font-bold tracking-tight',
    'Heading 2': 'text-3xl font-semibold tracking-tight',
    'Heading 3': 'text-2xl font-semibold tracking-tight',
    'Heading 4': 'text-xl font-semibold tracking-tight',
    'Heading 5': 'text-lg font-semibold tracking-tight',
    'Heading 6': 'text-base font-semibold tracking-tight',
};

const SUBTITLE_TYPOGRAPHY_CLASS: Partial<Record<Typography, string>> = {
    Paragraph: 'text-sm font-normal leading-6',
    'Heading 1': 'text-2xl font-bold tracking-tight',
    'Heading 2': 'text-xl font-semibold tracking-tight',
    'Heading 3': 'text-lg font-semibold tracking-tight',
    'Heading 4': 'text-base font-semibold tracking-tight',
    'Heading 5': 'text-sm font-semibold tracking-tight',
    'Heading 6': 'text-sm font-semibold tracking-tight',
};

// eslint-disable-next-line react-refresh/only-export-components
export { carouselItemImageWidths, productCarouselItemAspectRatio, carouselItemClassName } from './constants';

/**
 * CarouselSection is a shared layout shell for all carousel components in the storefront.
 *
 * It provides the consistent outer padding, title row, Embla carousel track, and prev/next
 * navigation buttons. Consumers render their own <CarouselItem> elements as children.
 *
 * @example
 * ```tsx
 * // Left-aligned title with shop-all link (product carousel style)
 * <CarouselSection title="Featured Products" shopAllUrl="/category/root" shopAllText="Shop all">
 *     {products.map(p => (
 *         <CarouselItem key={p.productId} className="basis-1/2 sm:basis-1/3 md:basis-1/4 pl-4 flex min-w-0">
 *             <ProductTile product={p} className="h-full w-full" />
 *         </CarouselItem>
 *     ))}
 * </CarouselSection>
 *
 * // Centered title with subtitle (category carousel style)
 * <CarouselSection title="Shop by Category" subtitle="Explore our collections" titleAlign="center">
 *     {categories.map(c => (
 *         <CarouselItem key={c.id} className="basis-1/2 sm:basis-1/3 md:basis-1/4 pl-4 flex min-w-0">
 *             <CategoryTile category={c} className="h-full w-full" />
 *         </CarouselItem>
 *     ))}
 * </CarouselSection>
 * ```
 */
export function CarouselSection({
    title,
    subtitle,
    shopAllUrl,
    shopAllText,
    titleClassName,
    titleColor,
    titleTypography,
    titleFontFamily,
    subtitleTypography,
    subtitleColor,
    subtitleFontFamily,
    titleAlign = 'left',
    className,
    ariaLabel,
    styleOverride,
    children,
}: CarouselSectionProps) {
    const { uid, scopedCss } = useScopedStyleOverride(styleOverride, 'data-carousel-section-id');

    const titleTypo = normalizeTypography(titleTypography);
    const subtitleTypo = normalizeTypography(subtitleTypography);
    const titleFontFamilyClass = FONT_FAMILY_CLASS[normalizeFontFamily(titleFontFamily)];
    const subtitleFontFamilyClass = FONT_FAMILY_CLASS[normalizeFontFamily(subtitleFontFamily)];

    const titleHex = parseHexColor(titleColor);
    const subtitleHex = parseHexColor(subtitleColor);
    const titleStyle: CSSProperties | undefined = titleHex ? { color: titleHex } : undefined;
    const subtitleStyle: CSSProperties | undefined = subtitleHex ? { color: subtitleHex } : undefined;

    // titleClassName remains a full-override escape hatch (pre-existing behavior) — the new
    // typography/color/font-family attributes only apply when it isn't set.
    const resolvedTitleClassName = (extra?: string) =>
        titleClassName ??
        cn(
            defaultTitleBaseClassName,
            TITLE_TYPOGRAPHY_CLASS[titleTypo],
            titleFontFamilyClass,
            !titleHex && 'text-foreground',
            extra
        );

    const resolvedSubtitleClassName = (base: string) =>
        cn(
            base,
            SUBTITLE_TYPOGRAPHY_CLASS[subtitleTypo],
            subtitleFontFamilyClass,
            !subtitleHex && 'text-muted-foreground'
        );

    const titleSection =
        titleAlign === 'center' ? (
            <div className="text-center">
                {title && (
                    <h2 className={resolvedTitleClassName('mb-4')} style={titleStyle}>
                        {title}
                    </h2>
                )}
                {subtitle && (
                    <p
                        className={resolvedSubtitleClassName('text-base font-normal leading-6 max-w-2xl mx-auto')}
                        style={subtitleStyle}>
                        {subtitle}
                    </p>
                )}
            </div>
        ) : (
            <div className="flex items-center justify-between">
                {subtitle ? (
                    <div>
                        {title && (
                            <h2 className={resolvedTitleClassName()} style={titleStyle}>
                                {title}
                            </h2>
                        )}
                        <p className={resolvedSubtitleClassName('text-sm mt-1')} style={subtitleStyle}>
                            {subtitle}
                        </p>
                    </div>
                ) : (
                    title && (
                        <h2 className={resolvedTitleClassName()} style={titleStyle}>
                            {title}
                        </h2>
                    )
                )}
                {shopAllText && (
                    <div>
                        {shopAllUrl ? (
                            <Link
                                to={shopAllUrl}
                                className="text-sm font-medium text-primary hover:text-primary/80 transition-colors shrink-0 ml-4">
                                {shopAllText}
                            </Link>
                        ) : (
                            <span className="text-sm font-medium text-primary shrink-0 ml-4">{shopAllText}</span>
                        )}
                    </div>
                )}
            </div>
        );

    // `w-full min-w-0`: `section-container` uses `mx-auto`, which makes a block shrink-to-fit when its
    // parent is a flex/grid container (Page Designer layouts, columns). Tiles are sized as a % of this
    // element, so a shrink-wrapped section collapses them; pin it to the parent's width instead.
    return (
        <div
            className={cn('section-container w-full min-w-0 py-6', className)}
            data-carousel-section-id={scopedCss ? uid : undefined}>
            {scopedCss && (
                // eslint-disable-next-line react/no-danger
                <style dangerouslySetInnerHTML={{ __html: scopedCss }} />
            )}
            {(title !== undefined || subtitle) && titleSection}
            <Carousel className="w-full py-6" opts={{ align: 'start' }} aria-label={ariaLabel}>
                <CarouselContent className="-ml-4 items-stretch flex-nowrap">{children}</CarouselContent>
                <CarouselPrevious className="flex left-1 sm:left-0 sm:-translate-x-1/2 size-9 rounded-none shadow-md z-10" />
                <CarouselNext className="flex right-1 sm:right-0 sm:translate-x-1/2 size-9 rounded-none shadow-md z-10" />
            </Carousel>
        </div>
    );
}
